import { requestRaw } from "@vjal/frontend-core";

export interface ApiClientOptions {
  baseUrl: string;
}

export interface ApiRequestOptions extends RequestInit {
  idempotencyKey?: string;
}

export interface ApiBlobResponse {
  blob: Blob;
  filename: string | null;
  contentType: string | null;
}

/**
 * Gate evidence parsed from non-report dark lanes. Report downloads now return
 * self-contained HTML attachments; their 503s are ordinary transient errors.
 */
export interface ApiClientGate {
  /** A `*Gate` value of "closed"/"open" (report generation / resource ingestion). */
  generationGate?: string | null;
  ingestionGate?: string | null;
  /** Zoom/meeting provisioning status, e.g. "not_created". */
  meetingProviderStatus?: string | null;
  /** True when the backend returned dry-run evidence (provider dark-lane). */
  dryRun?: boolean;
  /** A `downloadUrl:null` marker on a still-gated resource download lane. */
  downloadUrlNull?: boolean;
  /** Free-text neutral reason for a closed gate (never a stack/secret). */
  reason?: string | null;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly reference: string | null;
  /**
   * The backend error code (`CO_VALIDATION_FAILED`, `CO_PERMISSION_DENIED`, …)
   * parsed off the `{error:{code,message,reference}}` envelope. Optional and
   * defaulted so the existing test-locked `toMatchObject({status,message,reference})`
   * assertions remain unaffected — this is purely additive.
   */
  readonly code: string | null;
  /** Parsed dark-lane gate evidence, when present on the envelope. */
  readonly gate: ApiClientGate | null;

  constructor(
    message: string,
    status: number,
    reference: string | null,
    code: string | null = null,
    gate: ApiClientGate | null = null
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.reference = reference;
    this.code = code;
    this.gate = gate;
  }
}

export function createApiClient({ baseUrl }: ApiClientOptions) {
  async function request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);

    // Transport is delegated to @vjal/frontend-core (no timeout/no retry: the
    // intentional 503-as-gate-closed posture must surface, never be retried);
    // this client keeps full ownership of the error/gate envelope parsing.
    const response = await requestRaw(`${baseUrl}${path}`, {
      ...options,
      headers,
      credentials: "include"
    });
    const payload = await readJson(response);
    if (!response.ok) {
      // TODO(api-error-detail): enrich ApiClientError with backend code,
      // fieldErrors, retryAfter, and a sanitized details payload so the UI can
      // render field-level validation and support can triage by code
      // (audit 2026-06-12 medium).
      throw new ApiClientError(
        extractErrorMessage(payload, response.statusText),
        response.status,
        extractErrorReference(payload),
        extractErrorCode(payload),
        extractGateEvidence(payload)
      );
    }
    return payload as T;
  }

  /**
   * Text/blob response path for the CSV export route. requestText returns the
   * raw response body (text/csv) instead of parsing JSON, so the UI can stream
   * a download. Errors still surface a sanitized ApiClientError (no raw body
   * leak beyond the backend's own error envelope).
   */
  async function requestText(path: string, options: ApiRequestOptions = {}): Promise<string> {
    const headers = new Headers(options.headers);
    if (!headers.has("Accept")) headers.set("Accept", "text/csv, application/json");
    if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);

    const response = await requestRaw(`${baseUrl}${path}`, {
      ...options,
      headers,
      credentials: "include"
    });
    const text = await response.text();
    if (!response.ok) {
      const parsed = safeJsonParse(text);
      throw new ApiClientError(
        extractErrorMessage(parsed, response.statusText),
        response.status,
        extractErrorReference(parsed),
        extractErrorCode(parsed),
        extractGateEvidence(parsed)
      );
    }
    return text;
  }

  async function requestBlob(path: string, options: ApiRequestOptions = {}): Promise<ApiBlobResponse> {
    const headers = new Headers(options.headers);
    if (!headers.has("Accept")) headers.set("Accept", "text/html, application/octet-stream, application/json");
    if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);

    const response = await requestRaw(`${baseUrl}${path}`, {
      ...options,
      headers,
      credentials: "include"
    });
    if (!response.ok) {
      const text = await response.text();
      const parsed = safeJsonParse(text);
      throw new ApiClientError(
        extractErrorMessage(parsed, response.statusText),
        response.status,
        extractErrorReference(parsed),
        extractErrorCode(parsed),
        extractGateEvidence(parsed)
      );
    }
    return {
      blob: await response.blob(),
      filename: extractDownloadFilename(response.headers.get("Content-Disposition")),
      contentType: response.headers.get("Content-Type")
    };
  }

  return { request, requestText, requestBlob };
}

function safeJsonParse(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractDownloadFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/(?:^|;)\s*filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = header.match(/(?:^|;)\s*filename="([^"]+)"/i)?.[1] ?? header.match(/(?:^|;)\s*filename=([^;]+)/i)?.[1];
  let candidate = encoded;
  if (candidate) {
    try {
      candidate = decodeURIComponent(candidate);
    } catch {
      return null;
    }
  } else {
    candidate = plain?.trim();
  }
  if (!candidate || [...candidate].some((ch) => ch.charCodeAt(0) < 0x20 || ch === "\\" || ch === "/")) return null;
  return candidate;
}
async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

function extractErrorMessage(payload: unknown, fallback: string) {
  if (payload && typeof payload === "object" && "error" in payload) {
    const error = (payload as { error?: unknown }).error;
    if (typeof error === "string" && error.trim()) return error;
    // Live coaching error envelope: { error: { code, message, reference } }
    if (error && typeof error === "object") {
      const message = (error as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback || "Request failed";
}

function extractErrorReference(payload: unknown) {
  if (!payload || typeof payload !== "object") return null;
  const direct = referenceFrom(payload);
  if (direct) return direct;
  const error = (payload as { error?: unknown }).error;
  return error && typeof error === "object" ? referenceFrom(error) : null;
}

function referenceFrom(source: object) {
  const candidates = source as { reference?: unknown; error_ref?: unknown; errorRef?: unknown };
  for (const value of [candidates.reference, candidates.error_ref, candidates.errorRef]) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
}

// Parse the backend error code off the live coaching envelope
// (`{ error: { code, message, reference } }`) or a flat `{ code }` body. Returns
// null when absent so the honest-state map can fall back to the HTTP status.
function extractErrorCode(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const top = (payload as { code?: unknown }).code;
  if (typeof top === "string" && top.trim()) return top;
  const error = (payload as { error?: unknown }).error;
  if (error && typeof error === "object") {
    const nested = (error as { code?: unknown }).code;
    if (typeof nested === "string" && nested.trim()) return nested;
  }
  return null;
}

// Parse dark-lane gate evidence off the envelope without leaking secrets. The
// coaching API marks intentionally-closed lanes via status strings
// (`generationGate`/`ingestionGate:'closed'`, `meetingProviderStatus`, a
// `downloadUrl:null` marker, or `dryRun`/`dry_run` evidence). These are stable
// gate-closed / not-configured postures, not failures; the posture map reads
// them. Returns null when no gate markers are present.
function extractGateEvidence(payload: unknown): ApiClientGate | null {
  if (!payload || typeof payload !== "object") return null;
  const sources: Array<Record<string, unknown>> = [payload as Record<string, unknown>];
  const error = (payload as { error?: unknown }).error;
  if (error && typeof error === "object") sources.push(error as Record<string, unknown>);
  const evidence = (payload as { evidence?: unknown }).evidence;
  if (evidence && typeof evidence === "object") sources.push(evidence as Record<string, unknown>);

  const gate: ApiClientGate = {};
  let found = false;
  for (const source of sources) {
    const generationGate = stringField(source, "generationGate", "generation_gate");
    if (generationGate != null && gate.generationGate == null) {
      gate.generationGate = generationGate;
      found = true;
    }
    const ingestionGate = stringField(source, "ingestionGate", "ingestion_gate");
    if (ingestionGate != null && gate.ingestionGate == null) {
      gate.ingestionGate = ingestionGate;
      found = true;
    }
    const meetingProviderStatus = stringField(source, "meetingProviderStatus", "meeting_provider_status");
    if (meetingProviderStatus != null && gate.meetingProviderStatus == null) {
      gate.meetingProviderStatus = meetingProviderStatus;
      found = true;
    }
    const reason = stringField(source, "reason");
    if (reason != null && gate.reason == null) {
      gate.reason = reason;
      found = true;
    }
    if ("downloadUrl" in source && source.downloadUrl === null && !gate.downloadUrlNull) {
      gate.downloadUrlNull = true;
      found = true;
    }
    for (const key of ["dryRun", "dry_run"]) {
      if (source[key] === true && !gate.dryRun) {
        gate.dryRun = true;
        found = true;
      }
    }
  }
  return found ? gate : null;
}

function stringField(source: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
}
